import { useState } from 'react'
import { X, MapPin, Calendar, Trophy, Users, ExternalLink } from 'lucide-react'
import { TeamLogo } from './team-logo'
import { getTeamFullDescription } from '../../utils/teamDescriptions'
import { getContrastTextColor } from '../../utils/colorUtils'
import type { Team } from '../../types'

interface TeamDetailModalProps {
  team: Team | null
  isOpen: boolean
  onClose: () => void
}

export function TeamDetailModal({ team, isOpen, onClose }: TeamDetailModalProps) {
  if (!isOpen || !team) return null

  // Determine the appropriate text color for the X button based on background
  const primaryColor = team.primaryColor || '#42a5f5'
  const xButtonColor = getContrastTextColor(primaryColor)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div 
            className="px-6 py-4 border-b"
            style={{
              background: `linear-gradient(135deg, ${team.primaryColor || '#42a5f5'} 0%, ${team.secondaryColor || '#1976d2'} 100%)`
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Team Logo */}
                <div className="w-16 h-16 bg-white rounded-lg p-2 shadow-sm flex items-center justify-center">
                  <TeamLogo 
                    team={team} 
                    size={48} 
                    className="w-12 h-12" 
                  />
                </div>
                <div className="text-white">
                  <h2 className="text-2xl font-bold">{team.name}</h2>
                  <div className="flex items-center space-x-4 text-sm opacity-90">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      {team.city}
                    </span>
                    <span className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {team.league}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors 
                          fixed top-16 right-4 z-50 bg-black bg-opacity-75 rounded-full p-3 shadow-lg
                          md:relative md:top-auto md:right-auto md:bg-transparent md:rounded-none md:p-0 md:z-auto md:shadow-none
                          md:hover:opacity-80"
                style={{
                  // On desktop (md and up), use the contrast color
                  '--x-button-color': xButtonColor
                } as React.CSSProperties & { '--x-button-color': string }}
              >
                <X 
                  className="h-6 w-6 md:text-[var(--x-button-color)]" 
                />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Team Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Full Name:</span>
                    <span className="font-medium">{team.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">City:</span>
                    <span className="font-medium">{team.city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Abbreviation:</span>
                    <span className="font-medium">{team.abbreviation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">League:</span>
                    <span className="font-medium">{team.league}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sport:</span>
                    <span className="font-medium">{team.sport}</span>
                  </div>
                  {team.founded && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Founded:</span>
                      <span className="font-medium flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {team.founded}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Organization</h3>
                <div className="space-y-2">
                  {team.conference && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Conference:</span>
                      <span className="font-medium">{team.conference}</span>
                    </div>
                  )}
                  {team.division && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Division:</span>
                      <span className="font-medium">{team.division}</span>
                    </div>
                  )}
                  {team.state && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">State:</span>
                      <span className="font-medium">{team.state}</span>
                    </div>
                  )}
                  {team.country && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Country:</span>
                      <span className="font-medium">{team.country}</span>
                    </div>
                  )}
                </div>

                {/* Team Colors */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Team Colors</h4>
                  <div className="flex space-x-2">
                    {team.primaryColor && (
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: team.primaryColor }}
                        />
                        <span className="text-xs text-gray-600">{team.primaryColor}</span>
                      </div>
                    )}
                    {team.secondaryColor && (
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: team.secondaryColor }}
                        />
                        <span className="text-xs text-gray-600">{team.secondaryColor}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Team Information/History */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Trophy className="h-5 w-5 mr-2 text-[#42a5f5]" />
                About {team.name}
              </h3>
              <p className="text-gray-700 leading-relaxed">{getTeamFullDescription(team)}</p>
            </div>

            {/* Additional Links/Actions */}
            <div className="border-t pt-6">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(team.name + ' ' + team.league)}`, '_blank')}
                  className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Search Online
                </button>
                {team.league === 'NFL' && (
                  <button
                    onClick={() => window.open(`https://www.nfl.com/teams/${team.name?.toLowerCase().replace(/\s+/g, '-')}`, '_blank')}
                    className="flex items-center px-4 py-2 bg-[#42a5f5] hover:bg-[#1976d2] text-white rounded-lg transition-colors text-sm"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Official Site
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}